<!-- @format -->

<template>
	<el-upload
		ref="uploadRef"
		multiple
		:action="action"
		:on-success="handleUploadSuccess"
		:before-upload="handleBeforeUpload"
		:limit="limit"
		:on-error="handleUploadError"
		:on-exceed="handleExceed"
		:on-remove="handleRemove"
		:headers="headers"
		:file-list="fileList"
		:show-file-list="!showTable"
		:data="data"
		:disabled="disabled"
		v-bind="uploadProps"
	>
		<slot name="list-type">
			<el-button :disabled="disabled" type="primary">选择文件</el-button>
		</slot>
		<template #tip>
			<el-button v-if="onDownloadTemplate" type="primary" link style="margin-left: 10px" @click="onDownloadTemplate?.()"
				>下载模板</el-button
			>
			<div class="el-upload__tip">
				<slot name="top"></slot>
				<div v-if="showTip">
					请上传
					<template v-if="fileSize">
						大小不超过<b style="color: #f56c6c">{{ fileSize }}MB</b>
					</template>
					<template v-if="fileType">
						格式为<b style="color: #f56c6c">{{ fileType.join(',') }}</b>
					</template>
					的文件
				</div>
				<slot name="bottom"></slot>
			</div>
			<div v-if="showTable">
				<el-table :data="fileList" style="width: 100%">
					<el-table-column type="index" width="100" label="序号" />
					<el-table-column prop="name" label="文件名" />
					<el-table-column v-if="!isV1Ver" prop="createTime" label="上传时间" />
					<el-table-column label="操作" width="100">
						<template #default="scope">
							<el-tooltip content="预览" placement="top">
								<el-button type="primary" link icon="View" @click="previewFileFun(scope.row)"></el-button>
							</el-tooltip>
							<el-tooltip content="下载" placement="top">
								<el-button type="primary" link icon="Download" @click="handleDownload(scope.row)"></el-button>
							</el-tooltip>
							<el-tooltip content="删除" placement="top">
								<el-button
									:disabled="disabled"
									type="danger"
									link
									icon="Delete"
									@click="handleDelete(scope.$index)"
								></el-button>
							</el-tooltip>
						</template>
					</el-table-column>
				</el-table>
				<PcFilePreview v-if="visible" :id="previewFile.id" v-model="visible" :file-name="previewFile.name" />
			</div>
		</template>
		<template #file="{ file }">
			<!-- index拿不到值 -->
			<!-- 样式在style里,打包到项目中,会导致报错，不知道为什么 -->
			<div
				v-if="action !== '' && action !== '#'"
				style="position: relative; display: flex; justify-content: space-between"
			>
				<div class="name">{{ file.name }}</div>
				<div class="btn">
					<el-tooltip content="预览" placement="top">
						<el-button type="primary" link icon="View" @click="previewFileFun(file)"></el-button>
					</el-tooltip>
					<el-tooltip content="下载" placement="top">
						<el-button type="primary" link icon="Download" @click="handleDownload(file)"></el-button>
					</el-tooltip>
					<el-tooltip content="删除" placement="top">
						<el-button
							:disabled="disabled"
							type="danger"
							link
							icon="Delete"
							@click="handleDelete(getFileIndex(file))"
						></el-button>
					</el-tooltip>
				</div>
				<PcFilePreview v-if="visible" :id="previewFile.id" v-model="visible" :file-name="previewFile.name" />
			</div>
		</template>
	</el-upload>
</template>

<script lang="ts" setup>
	import { downloadByUrl, getFileUrlById, getToken, getEnvUrlByUrl } from '@power/runtime-vue3';
	import { genFileId } from 'element-plus';
	import type { UploadInstance, UploadUserFile } from 'element-plus';
	import { PcModal as modal, PcFilePreview, getFileTypePcFilePreview } from '@/components';
	import { resource } from '@/api/global/index';
	const uploadRef = ref<UploadInstance>();

	const isV1Ver = 'V1' === window.pData.env.VITE_ENV_APP_BASE_API_VERSION;

	const props = defineProps({
		modelValue: {
			type: Array,
		},
		// 文件数量限制
		limit: {
			type: Number,
			default: 5,
		},
		// 大小限制(MB)
		fileSize: {
			type: Number,
			default: 5,
		},
		fileType: {
			type: Array,
			default: () => ['.png', '.jpg', '.jpeg'],
		},
		// 是否显示提示
		isShowTip: {
			type: Boolean,
			default: true,
		},
		action: {
			type: String,
			default: `${getEnvUrlByUrl('power-api/base/oss/file/upload')}`,
			// default:
			// 	'V1' === window.pData.env.VITE_ENV_APP_BASE_API_VERSION
			// 		? `${getEnvUrlByUrl('power-api/file/upload')}`
			// 		: `${getEnvUrlByUrl('power-api/base/oss/file/upload')}`,
		},
		uploadProps: {
			type: Object,
		},
		showTable: {
			type: Boolean,
			default: false,
		},
		data: {
			type: Object,
			default: () => {
				return { folderAlias: '', groupKey: '' };
			},
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		onDownloadTemplate: {
			type: Function,
		},
		replaceFile: {
			type: Boolean,
			default: false,
		},
	});

	const emit = defineEmits(['update:modelValue']);
	const headers = ref({ Authorization: `Bearer ${getToken()}` });
	const number = ref(0);
	const uploadList = ref<any[]>([]);
	const showTip = computed(() => props.isShowTip && (props.fileType || props.fileSize));
	const fileList = ref<UploadUserFile[]>([]);
	const previewFile = ref({
		id: '',
		name: '',
	});
	const visible = ref(false);
	const getFileIndex = (file) => {
		return fileList.value.findIndex((item: any) => item.id === file.id);
	};
	watch(
		() => props.modelValue,
		async (newVal, oldVal) => {
			if (newVal && newVal !== oldVal && newVal.length) {
				const arr: any = [];
				for (let i = 0; i < newVal.length; i++) {
					if (isV1Ver) {
						const { name, url } = newVal[i] as any;
						arr.push({ name, url });
					} else {
						const res: any = await resource(newVal[i]);
						const url = getFileUrlById(newVal[i] as string);
						arr.push({
							name: res.data?.resourceName || '',
							createTime: res.data?.createTime || '',
							id: newVal[i],
							url,
						});
					}
				}
				fileList.value = arr;
			} else {
				fileList.value = [];
				return [];
			}
		},
		{ deep: true, immediate: true },
	);

	// 删除
	function handleRemove(_file, _files) {
		updateValue();
	}

	function handleDelete(index) {
		fileList.value.splice(index, 1);
		updateValue();
	}

	function handleDownload(row) {
		let url = '';
		if (isV1Ver) {
			url = row.url;
		} else {
			url = `${window.location.origin}${row.url}`;
		}
		downloadByUrl({ url, fileName: row.name });
	}

	// 上传成功回调
	async function handleUploadSuccess(res) {
		if (isV1Ver) {
			uploadList.value.push({ name: res.data.name, url: res.data.url });
		} else {
			const url = getFileUrlById(res.data.id);
			uploadList.value.push({
				name: res.data.resourceName,
				url,
				createTime: res.data.createTime,
				id: res.data.id,
			});
		}
		if (uploadList.value.length === number.value) {
			fileList.value = fileList.value.concat(uploadList.value);
			uploadList.value = [];
			number.value = 0;
			updateValue();
			modal.closeLoading();
		}
	}

	function updateValue() {
		if (isV1Ver) {
			emit('update:modelValue', fileList.value);
		} else {
			const idArr = fileList.value.map((itm: any) => {
				return itm.id;
			});
			emit('update:modelValue', idArr);
		}
	}

	// 上传前loading加载
	function handleBeforeUpload(file) {
		let isTypeOk = false;
		if (props.fileType.length) {
			let fileExtension = '';
			if (file.name.lastIndexOf('.') > -1) {
				fileExtension = file.name.slice(file.name.lastIndexOf('.'));
			}
			isTypeOk = props.fileType.some((type: any) => {
				if (file.type.indexOf(type) > -1) return true;
				if (fileExtension && fileExtension.indexOf(type) > -1) return true;
				return false;
			});
		} else {
			isTypeOk = true;
		}
		if (!isTypeOk) {
			modal.msgError(`文件格式不正确, 请上传${props.fileType.join(',')}格式文件!`);
			return false;
		}
		if (props.fileSize) {
			const isLt = file.size / 1024 / 1024 < props.fileSize;
			if (!isLt) {
				modal.msgError(`上传大小不能超过 ${props.fileSize} MB!`);
				return false;
			}
		}
		modal.loading('正在上传，请稍候...');
		number.value++;
	}

	// 文件个数超出
	function handleExceed(files) {
		if (props.replaceFile) {
			uploadRef.value!.clearFiles();
			fileList.value = [];
			nextTick(() => {
				const file = files[0];
				file.uid = genFileId();
				uploadRef.value!.handleStart(file);
				uploadRef.value!.submit();
			});
		} else {
			modal.msgError(`上传文件数量不能超过 ${props.limit} 个!`);
		}
	}

	// 上传失败
	function handleUploadError() {
		modal.msgError('上传失败');
		modal.closeLoading();
	}

	// function handleClick(row) {
	// 	// const fileName = row.name;
	// 	// const isOffice =
	// 	// 	fileName.endsWith('.doc') ||
	// 	// 	fileName.endsWith('.docx') ||
	// 	// 	fileName.endsWith('.xls') ||
	// 	// 	fileName.endsWith('.xlsx') ||
	// 	// 	fileName.endsWith('.ppt') ||
	// 	// 	fileName.endsWith('.pptx');
	// 	// const isImage =
	// 	// 	fileName.endsWith('.jpg') ||
	// 	// 	fileName.endsWith('.jpeg') ||
	// 	// 	fileName.endsWith('.jpe') ||
	// 	// 	fileName.endsWith('.png') ||
	// 	// 	fileName.endsWith('.bmp');
	// 	// if (isOffice) {
	// 	// } else if (fileName.endsWith('.js') || fileName.endsWith('.txt') || fileName.endsWith('.pdf') || isImage) {
	// 	// } else {
	// 	// 	handleDownload(row);
	// 	// }
	// 	handleDownload(row);
	// }

	const previewFileFun = (row) => {
		const type = getFileTypePcFilePreview(row.name);
		if (type === 'unsupported') {
			ElMessage.warning('该文件不支持预览,请下载后查看');
			return false;
		}
		previewFile.value = {
			id: row.id,
			name: row.name,
		};
		visible.value = true;
	};

	defineExpose({ uploadRef, handleUploadSuccess, fileList });
</script>

<style lang="scss" scoped></style>
